// Mock data for the GTM Plays platform
export interface Program {
  id: string
  title: string
  summary: string
  business_objective: 'pipeline' | 'revenue' | 'adoption' | 'retention'
  time_horizon_days: number
  budget: {
    currency: string
    amount: number
  }
  headcount_fte: number
  stage_coverage: string[]
  kpis: Array<{
    name: string
    target: number
    current?: number
  }>
  attached_playbook_ids: string[]
  owner: {
    name: string
    team: string
  }
  notes_md: string
  tags: string[]
  status: 'active' | 'draft' | 'paused'
  health: 'healthy' | 'at-risk' | 'critical'
}
export interface Playbook {
  id: string
  title: string
  summary: string
  icon: string
  stages: string[]
  primary_kpis: string[]
  secondary_kpis: string[]
  industry_fit: string[]
  personas: string[]
  plays: string[]
  owner: {
    name: string
    team: string
  }
  complexity: 'simple' | 'standard' | 'advanced'
  duration_days: number
  assets: Array<{
    type: string
    ref: string
    name: string
  }>
  tags: string[]
  updated_at: string
  lifecycle: string[]
  motion: string[]
  signals: string[]
  agents: string[]
  kpis: string[]
  status: 'draft' | 'published' | 'deprecated'
  expected_lift: string
  time_to_value: string
  data_needed: string[]
  triggers: string[]
  eligibility: {
    account_fit: string[]
    buying_group_roles: string[]
    exclusions: string[]
  }
  ai_ready: boolean
  activation_rate?: number
}
export interface PlayStep {
  day: number
  type: string
  template_ref?: string
  owner: string
  sla_hours: number
  copy_ref?: string
  segment_ref?: string
  script_ref?: string
  provider?: string
}
export interface Agent {
  id: string
  name: string
  summary: string
  inputs: string[]
  actions: string[]
  limits: {
    daily_sends?: number
    auto_reply?: boolean
  }
  owner_team: string
  capabilities: string[]
}
export interface Play {
  id: string
  title: string
  summary: string
  objective: string
  stage: string
  motion: string[]
  personas: string[]
  account_tier: string[]
  trigger: {
    logic_md: string
    data_sources: string[]
    examples: string[]
  }
  eligibility: {
    account_fit: {
      tier: string
      min_profile_score: number
    }
    buying_group_roles: string[]
    exclusions: string[]
  }
  audience: {
    personas: string[]
    geo: string[]
  }
  channels: string[]
  steps: PlayStep[]
  agents: Array<{
    name: string
    capabilities: string[]
    inputs: string[]
  }>
  guardrails: {
    frequency_caps: {
      email_per_7d: number
    }
    exclusions: string[]
  }
  measurement: {
    primary: string[]
    secondary: string[]
    success_definition_md: string
    estimated_impact: string
  }
  evidence: Array<{
    type: string
    ref: string
  }>
  compliance: {
    gdpr_safe: boolean
    can_spam: boolean
  }
  taxonomy: {
    program_ids: string[]
    playbook_ids: string[]
    tags: string[]
  }
  zoominfo_features: string[]
  complexity: 'simple' | 'standard' | 'advanced'
  ai_readiness: 'native' | 'partial' | 'manual'
  time_to_implement: string
  performance_badge?: string
  source?: 'zoominfo' | 'community' | 'custom'
  updated_at: string
  variants?: Array<{
    name: string
    description: string
  }>
  dependencies?: string[]
}
// Mock Programs
export const mockProgramsNew: Program[] = [
  {
    id: 'prog-001',
    title: 'Q1 Enterprise New Logo Acquisition',
    summary:
      'Multi-channel ABM program targeting Fortune 500 accounts showing high buying intent',
    business_objective: 'pipeline',
    time_horizon_days: 90,
    budget: {
      currency: 'USD',
      amount: 450000,
    },
    headcount_fte: 12,
    stage_coverage: [
      'Awareness',
      'Engagement',
      'Evaluation',
      'Negotiation',
      'Commit',
    ],
    kpis: [
      { name: 'Pipeline Created', target: 5000000, current: 3200000 },
      { name: 'Meetings Booked', target: 150, current: 98 },
      { name: 'Opportunities Created', target: 45, current: 28 },
    ],
    attached_playbook_ids: ['pb-001', 'pb-002'],
    owner: {
      name: 'Sarah Chen',
      team: 'Growth',
    },
    notes_md:
      'Focus on tech vertical with recent funding rounds. Prioritize accounts with 3+ intent signals.',
    tags: ['New Logo', 'Enterprise', 'Tech', 'ABM'],
    status: 'active',
    health: 'healthy',
  },
  {
    id: 'prog-002',
    title: 'PLG Activation & Conversion',
    summary:
      'Product-led growth program to activate trial users and drive paid conversions',
    business_objective: 'revenue',
    time_horizon_days: 180,
    budget: {
      currency: 'USD',
      amount: 280000,
    },
    headcount_fte: 8,
    stage_coverage: ['Onboarding', 'Activation', 'Impact'],
    kpis: [
      { name: 'Trial to Paid Rate', target: 18, current: 14.5 },
      { name: 'Activation Rate', target: 65, current: 58 },
      { name: 'Time to Value (days)', target: 7, current: 9 },
    ],
    attached_playbook_ids: ['pb-003'],
    owner: {
      name: 'Mike Rodriguez',
      team: 'Product Growth',
    },
    notes_md:
      'Leverage in-app messaging and AI email agents for personalized onboarding.',
    tags: ['PLG', 'SMB', 'Conversion'],
    status: 'active',
    health: 'at-risk',
  },
]
// Mock Playbooks
export const mockPlaybooksNew: Playbook[] = [
  {
    id: 'pb-001',
    title: 'Intent-Based Engagement',
    icon: '🎯',
    summary:
      'Detect buying signals across multiple channels and accelerate outreach to increase meeting conversion within defined timeframes.',
    lifecycle: ['Acquire'],
    motion: ['ABM', 'Outbound'],
    stages: ['Awareness', 'Engagement', 'Evaluation'],
    primary_kpis: [
      'meetings_booked',
      'meeting_conversion_time',
      'pipeline_created',
    ],
    secondary_kpis: ['response_rate', 'engagement_score'],
    industry_fit: ['SaaS', 'Tech', 'FinTech'],
    personas: ['Marketing', 'Sales'],
    plays: ['play-001', 'play-003', 'play-007'],
    signals: ['zoominfo_intent', 'web_pricing_views', 'competitor_page'],
    agents: ['agent-001', 'agent-002'],
    kpis: ['Meetings Booked', 'Meeting Conversion Time', 'Pipeline Created'],
    owner: {
      name: 'Sarah Chen',
      team: 'Growth Ops',
    },
    complexity: 'standard',
    duration_days: 21,
    expected_lift: '+25-30%',
    time_to_value: '7-14 days',
    data_needed: ['Intent', 'Enrichment', 'Buying Groups'],
    triggers: [
      'Intent topic score 70+',
      '2+ high value page views in 7d',
      'Competitor research page hit',
      'Pricing page recency',
    ],
    eligibility: {
      account_fit: ['ICP tier 1 or 2', 'No active opportunity'],
      buying_group_roles: ['Economic Buyer', 'Champion', 'Technical Buyer'],
      exclusions: ['open_opportunity', 'do_not_contact', 'recent_meeting_30d'],
    },
    assets: [
      { type: 'email_template', ref: 'tmpl-001', name: 'Intent Surge Email' },
      {
        type: 'linkedin_sequence',
        ref: 'tmpl-002',
        name: 'Executive Outreach',
      },
      { type: 'ad_creative', ref: 'tmpl-003', name: 'Retargeting Ads' },
    ],
    tags: ['Signal-First', 'Agent-Ready', 'ABM'],
    status: 'published',
    ai_ready: true,
    activation_rate: 78,
    updated_at: '2024-01-15T10:30:00Z',
  },
  {
    id: 'pb-002',
    title: 'Opportunity Re-Engagement',
    icon: '🔄',
    summary:
      'Identify dormant deals and gaps in buying committee coverage, then deploy targeted campaigns to revive stalled pipeline.',
    lifecycle: ['Close'],
    motion: ['ABM'],
    stages: ['Evaluation', 'Negotiation', 'Commit'],
    primary_kpis: [
      'stage_progression_rate',
      'reopened_opps',
      'win_rate_reactivated',
    ],
    secondary_kpis: ['engagement_score', 'buying_group_coverage'],
    industry_fit: ['SaaS', 'Tech', 'FinTech', 'Manufacturing'],
    personas: ['Sales', 'RevOps'],
    plays: ['play-002', 'play-003'],
    signals: [
      'opp_idle_21d',
      'missing_finance_role',
      'negative_engagement_trend',
    ],
    agents: ['agent-002', 'agent-003'],
    kpis: [
      'Stage Progression Rate',
      'Reopened Opps',
      'Win Rate on Reactivated Opps',
    ],
    owner: {
      name: 'Mike Rodriguez',
      team: 'Sales Ops',
    },
    complexity: 'advanced',
    duration_days: 30,
    expected_lift: '+15-20%',
    time_to_value: '14-21 days',
    data_needed: ['Buying Groups', 'Enrichment', 'CRM Data'],
    triggers: [
      'Opportunity idle 21d',
      'Missing finance persona',
      'Missing IT/security persona',
      'Negative engagement trend',
    ],
    eligibility: {
      account_fit: ['Active opportunity', 'Enterprise segment'],
      buying_group_roles: ['Economic Buyer', 'Technical Buyer', 'Champion'],
      exclusions: ['lost_deal', 'stalled_opportunity'],
    },
    assets: [
      { type: 'email_template', ref: 'tmpl-004', name: 'Idle Opp Nudge' },
      {
        type: 'email_template',
        ref: 'tmpl-005',
        name: 'Missing Finance Persona',
      },
      { type: 'script', ref: 'tmpl-006', name: 'AE Objection Coach' },
    ],
    tags: ['Agent-Ready', 'ABM'],
    status: 'published',
    ai_ready: true,
    activation_rate: 65,
    updated_at: '2024-01-18T14:20:00Z',
  },
  {
    id: 'pb-003',
    title: 'Event-Driven Pipeline',
    icon: '🎪',
    summary:
      'Orchestrate pre-event targeting, real-time engagement, and post-event follow-up to convert attendees into qualified pipeline.',
    lifecycle: ['Acquire', 'Close'],
    motion: ['Event', 'ABM'],
    stages: ['Awareness', 'Engagement', 'Evaluation'],
    primary_kpis: ['meetings_48h', 'opps_21d', 'event_sourced_pipeline'],
    secondary_kpis: ['attendance_rate', 'booth_engagement'],
    industry_fit: ['SaaS', 'Tech', 'Healthcare', 'FinTech'],
    personas: ['Marketing', 'Sales'],
    plays: ['play-004'],
    signals: [
      'event_registered',
      'badge_scan',
      'session_attended',
      'booth_visit',
    ],
    agents: ['agent-003', 'agent-001'],
    kpis: ['Meetings in 48h', 'Opps in 21d', 'Event Sourced Pipeline'],
    owner: {
      name: 'Sarah Chen',
      team: 'Growth',
    },
    complexity: 'advanced',
    duration_days: 45,
    expected_lift: '+35-40%',
    time_to_value: '30-45 days',
    data_needed: ['Event Data', 'Intent', 'Enrichment'],
    triggers: [
      'Registered for event',
      'Badge scan',
      'Session attended',
      'Booth visit',
    ],
    eligibility: {
      account_fit: ['Target account', 'Event attendee'],
      buying_group_roles: ['Any'],
      exclusions: ['active_customer', 'do_not_contact'],
    },
    assets: [
      { type: 'email_sequence', ref: 'tmpl-007', name: 'Event Pre-Warm' },
      { type: 'landing_page', ref: 'tmpl-008', name: 'Event Registration' },
      { type: 'email_sequence', ref: 'tmpl-009', name: 'Post-Event Nurture' },
    ],
    tags: ['Event', 'Agent-Ready', 'ABM'],
    status: 'published',
    ai_ready: true,
    activation_rate: 82,
    updated_at: '2024-01-20T09:15:00Z',
  },
  {
    id: 'pb-004',
    title: 'Conversion Optimization',
    icon: '⚡',
    summary:
      'Identify and address funnel bottlenecks with targeted interventions, content, and automation at critical transition points.',
    lifecycle: ['Close'],
    motion: ['Inbound', 'ABM'],
    stages: ['Evaluation', 'Negotiation'],
    primary_kpis: ['mql_to_sql', 'sql_to_opp', 'time_to_first_meeting'],
    secondary_kpis: ['stage_conversion', 'drop_off_rate'],
    industry_fit: ['SaaS', 'Tech'],
    personas: ['Marketing', 'RevOps'],
    plays: ['play-001', 'play-003'],
    signals: [
      'mql_to_sql_drop',
      'sql_to_opp_drop',
      'multi_persona_not_reached',
    ],
    agents: ['agent-001', 'agent-005'],
    kpis: ['MQL to SQL', 'SQL to Opp', 'Time to First Meeting'],
    owner: {
      name: 'Mike Rodriguez',
      team: 'RevOps',
    },
    complexity: 'standard',
    duration_days: 21,
    expected_lift: '+20-25%',
    time_to_value: '14-21 days',
    data_needed: ['CRM Data', 'Enrichment'],
    triggers: [
      'MQL to SQL drop',
      'SQL to opp drop',
      'Multi persona not reached',
      'Disqualified for timing',
    ],
    eligibility: {
      account_fit: ['Active in funnel', 'ICP match'],
      buying_group_roles: ['Any'],
      exclusions: ['do_not_contact'],
    },
    assets: [
      {
        type: 'email_template',
        ref: 'tmpl-010',
        name: 'Conversion Guardrail Email',
      },
      { type: 'content', ref: 'tmpl-011', name: 'Persona Fill In Content' },
      { type: 'script', ref: 'tmpl-012', name: 'Content Assist for AE' },
    ],
    tags: ['Agent-Ready'],
    status: 'published',
    ai_ready: true,
    activation_rate: 71,
    updated_at: '2024-01-22T11:45:00Z',
  },
  {
    id: 'pb-005',
    title: 'Product-Led Expansion',
    icon: '📈',
    summary:
      'Use usage patterns and firmographic fit to trigger cross-sell and upsell motions through automated workflows.',
    lifecycle: ['Expand'],
    motion: ['PLG', 'CS'],
    stages: ['Activation', 'Impact', 'Expansion'],
    primary_kpis: ['expansion_arr', 'feature_adoption', 'time_to_upsell'],
    secondary_kpis: ['usage_spike_detection', 'nrr'],
    industry_fit: ['SaaS', 'Tech'],
    personas: ['CS', 'Sales'],
    plays: ['play-005', 'play-006'],
    signals: [
      'product_feature_use_hit',
      'usage_spike',
      'seat_cap_80_percent',
      'ai_module_explored',
    ],
    agents: ['agent-001', 'agent-004'],
    kpis: ['Expansion ARR', 'Feature Adoption', 'Time to Upsell'],
    owner: {
      name: 'Mike Rodriguez',
      team: 'Product Growth',
    },
    complexity: 'standard',
    duration_days: 30,
    expected_lift: '+30-35%',
    time_to_value: '21-30 days',
    data_needed: ['Product Telemetry', 'Enrichment'],
    triggers: [
      'Product feature use hit',
      'Usage spike',
      'Seat cap at 80%',
      'AI module explored',
    ],
    eligibility: {
      account_fit: ['Active customer', 'Good health score'],
      buying_group_roles: ['Economic Buyer', 'Champion'],
      exclusions: ['at_risk_customer', 'expansion_in_progress'],
    },
    assets: [
      {
        type: 'email_template',
        ref: 'tmpl-013',
        name: 'Usage Spike to AI Add On',
      },
      {
        type: 'email_template',
        ref: 'tmpl-014',
        name: 'Expansion AI Bundle Offer',
      },
      { type: 'script', ref: 'tmpl-015', name: 'Champion Move Detection' },
    ],
    tags: ['PLG', 'Agent-Ready'],
    status: 'published',
    ai_ready: true,
    activation_rate: 88,
    updated_at: '2024-01-25T08:30:00Z',
  },
  {
    id: 'pb-006',
    title: 'Account Warming Programs',
    icon: '🔥',
    summary:
      'Build sustained engagement with target accounts over time through multi-touch, multi-channel nurture sequences before active outreach.',
    lifecycle: ['Acquire'],
    motion: ['ABM', 'Outbound'],
    stages: ['Awareness', 'Engagement'],
    primary_kpis: [
      'account_engagement_score',
      'warm_to_meeting_conversion',
      'buying_group_reach',
    ],
    secondary_kpis: ['content_engagement', 'brand_awareness'],
    industry_fit: ['SaaS', 'Tech', 'FinTech', 'Manufacturing'],
    personas: ['Marketing', 'Sales'],
    plays: ['play-001', 'play-004'],
    signals: [
      'icp_tier_1_or_2',
      'no_active_opportunity',
      'low_engagement_last_30d',
    ],
    agents: ['agent-001', 'agent-005'],
    kpis: [
      'Account Engagement Score',
      'Warm to Meeting Conversion',
      'Reach Across Buying Group',
    ],
    owner: {
      name: 'Sarah Chen',
      team: 'Growth',
    },
    complexity: 'simple',
    duration_days: 60,
    expected_lift: '+18-22%',
    time_to_value: '45-60 days',
    data_needed: ['Intent', 'Enrichment', 'Buying Groups'],
    triggers: [
      'ICP tier 1 or 2',
      'No active opportunity',
      'Low engagement last 30d',
    ],
    eligibility: {
      account_fit: ['ICP tier 1 or 2', 'No active opportunity'],
      buying_group_roles: ['Any'],
      exclusions: ['active_opportunity', 'do_not_contact'],
    },
    assets: [
      {
        type: 'email_sequence',
        ref: 'tmpl-016',
        name: 'ABM 1-to-1 Warm Intro',
      },
      {
        type: 'content',
        ref: 'tmpl-017',
        name: 'High Value Content Syndication',
      },
      { type: 'email_sequence', ref: 'tmpl-018', name: 'SDR 3 Touch Assist' },
    ],
    tags: ['ABM', 'Agent-Ready'],
    status: 'published',
    ai_ready: true,
    activation_rate: 62,
    updated_at: '2024-01-28T10:15:00Z',
  },
  {
    id: 'pb-007',
    title: 'Win-Back Campaigns',
    icon: '🎁',
    summary:
      'Re-engage churned customers or lost opportunities with personalized messaging based on time elapsed and changing circumstances.',
    lifecycle: ['Expand'],
    motion: ['CS', 'ABM'],
    stages: ['Re-engagement', 'Evaluation'],
    primary_kpis: [
      'meetings_from_win_back',
      're_won_arr',
      'list_reactivation_rate',
    ],
    secondary_kpis: ['response_rate', 'engagement_score'],
    industry_fit: ['SaaS', 'Tech'],
    personas: ['CS', 'Sales'],
    plays: ['play-002'],
    signals: [
      'closed_lost_6_to_12_months',
      'churned_6_to_12_months',
      'competitor_identified',
    ],
    agents: ['agent-001'],
    kpis: ['Meetings from Win Back', 'Re-won ARR', 'List Reactivation Rate'],
    owner: {
      name: 'Mike Rodriguez',
      team: 'Customer Success',
    },
    complexity: 'standard',
    duration_days: 45,
    expected_lift: '+12-15%',
    time_to_value: '30-45 days',
    data_needed: ['CRM Data', 'Enrichment'],
    triggers: [
      'Closed lost in last 6-12 months',
      'Churned in last 6-12 months',
      'Competitor identified',
    ],
    eligibility: {
      account_fit: ['Churned or lost', 'Time elapsed 6-12 months'],
      buying_group_roles: ['Economic Buyer', 'Champion'],
      exclusions: ['active_customer', 'do_not_contact'],
    },
    assets: [
      {
        type: 'email_template',
        ref: 'tmpl-019',
        name: 'Closed Lost Reason Revisit',
      },
      {
        type: 'email_template',
        ref: 'tmpl-020',
        name: 'New Capability Outreach',
      },
      { type: 'script', ref: 'tmpl-021', name: 'Competitive Save' },
    ],
    tags: ['Agent-Ready'],
    status: 'published',
    ai_ready: true,
    activation_rate: 45,
    updated_at: '2024-01-30T14:20:00Z',
  },
  {
    id: 'pb-008',
    title: 'Champion Activation',
    icon: '👑',
    summary:
      'Identify and mobilize internal advocates within target accounts to accelerate deal progression and expand within existing customers.',
    lifecycle: ['Close', 'Expand'],
    motion: ['ABM', 'CS'],
    stages: ['Evaluation', 'Negotiation', 'Expansion'],
    primary_kpis: [
      'multi_thread_rate',
      'opp_velocity',
      'expansion_opportunity_creation',
    ],
    secondary_kpis: ['champion_engagement', 'deal_velocity'],
    industry_fit: ['SaaS', 'Tech', 'FinTech'],
    personas: ['Sales', 'CS'],
    plays: ['play-003'],
    signals: [
      'champion_promoted',
      'new_stakeholder_hired',
      'new_division_added',
      'product_champion_posted',
    ],
    agents: ['agent-002', 'agent-003'],
    kpis: [
      'Multi-thread Rate',
      'Opp Velocity',
      'Expansion Opportunity Creation',
    ],
    owner: {
      name: 'Sarah Chen',
      team: 'Growth',
    },
    complexity: 'advanced',
    duration_days: 21,
    expected_lift: '+28-32%',
    time_to_value: '14-21 days',
    data_needed: ['Buying Groups', 'Enrichment', 'Product Telemetry'],
    triggers: [
      'Champion promoted',
      'New stakeholder hired',
      'New division added',
      'Product champion posted',
    ],
    eligibility: {
      account_fit: ['Active opportunity or customer', 'Enterprise segment'],
      buying_group_roles: ['Champion', 'Economic Buyer'],
      exclusions: ['at_risk_customer'],
    },
    assets: [
      {
        type: 'email_template',
        ref: 'tmpl-022',
        name: 'Champion Congrats + CTA',
      },
      { type: 'social', ref: 'tmpl-023', name: 'Social Signal Follow Up' },
      {
        type: 'email_sequence',
        ref: 'tmpl-024',
        name: 'Multi Thread to New Team',
      },
    ],
    tags: ['Agent-Ready', 'ABM'],
    status: 'published',
    ai_ready: true,
    activation_rate: 76,
    updated_at: '2024-02-01T09:30:00Z',
  },
  {
    id: 'pb-009',
    title: 'Competitive Displacement',
    icon: '⚔️',
    summary:
      'Target accounts using competitor solutions with tailored messaging and comparison content when switching signals appear.',
    lifecycle: ['Acquire'],
    motion: ['ABM', 'Outbound'],
    stages: ['Awareness', 'Engagement', 'Evaluation'],
    primary_kpis: [
      'competitive_opps_created',
      'win_rate_against_competitor',
      'time_to_opportunity',
    ],
    secondary_kpis: ['meeting_rate', 'engagement_score'],
    industry_fit: ['SaaS', 'Tech'],
    personas: ['Marketing', 'Sales'],
    plays: ['play-002'],
    signals: [
      'competitor_technology_detected',
      'job_change_into_our_icp',
      'intent_on_replacement_topics',
    ],
    agents: ['agent-001'],
    kpis: [
      'Competitive Opps Created',
      'Win Rate Against Competitor',
      'Time to Opportunity',
    ],
    owner: {
      name: 'Sarah Chen',
      team: 'Growth',
    },
    complexity: 'advanced',
    duration_days: 30,
    expected_lift: '+22-27%',
    time_to_value: '21-30 days',
    data_needed: ['Technographics', 'Intent', 'Enrichment'],
    triggers: [
      'Competitor technology detected',
      'Job change into our ICP',
      'Intent on replacement topics',
    ],
    eligibility: {
      account_fit: ['Using competitor', 'ICP match'],
      buying_group_roles: ['Economic Buyer', 'Technical Buyer'],
      exclusions: ['active_customer', 'do_not_contact'],
    },
    assets: [
      {
        type: 'landing_page',
        ref: 'tmpl-025',
        name: 'Competitive Landing Experience',
      },
      { type: 'script', ref: 'tmpl-026', name: 'AE Competitive Coach' },
      {
        type: 'email_sequence',
        ref: 'tmpl-027',
        name: 'Displacement Offer Follow Up',
      },
    ],
    tags: ['Agent-Ready', 'ABM'],
    status: 'published',
    ai_ready: true,
    activation_rate: 68,
    updated_at: '2024-02-03T12:45:00Z',
  },
  {
    id: 'pb-010',
    title: 'Seasonal & Trigger-Based',
    icon: '📅',
    summary:
      'Deploy programs tied to fiscal cycles, industry events, regulatory changes, or other external triggers relevant to your ICP.',
    lifecycle: ['Acquire'],
    motion: ['Outbound', 'Event'],
    stages: ['Awareness', 'Engagement'],
    primary_kpis: [
      'in_period_meeting_volume',
      'in_period_opps',
      'email_engagement',
    ],
    secondary_kpis: ['response_rate', 'timing_accuracy'],
    industry_fit: ['SaaS', 'Tech', 'FinTech', 'Healthcare'],
    personas: ['Marketing', 'Sales'],
    plays: ['play-001', 'play-004'],
    signals: [
      'quarter_end',
      'year_end',
      'fiscal_year_change',
      'industry_specific_event',
      'regulatory_change',
    ],
    agents: ['agent-001'],
    kpis: ['In-Period Meeting Volume', 'In-Period Opps', 'Email Engagement'],
    owner: {
      name: 'Sarah Chen',
      team: 'Growth',
    },
    complexity: 'simple',
    duration_days: 14,
    expected_lift: '+20-24%',
    time_to_value: '7-14 days',
    data_needed: ['Intent', 'Enrichment'],
    triggers: [
      'Quarter end',
      'Year end',
      'Fiscal year change',
      'Industry specific event',
      'Regulatory change',
    ],
    eligibility: {
      account_fit: ['ICP match', 'In target industry'],
      buying_group_roles: ['Any'],
      exclusions: ['do_not_contact'],
    },
    assets: [
      { type: 'email_template', ref: 'tmpl-028', name: 'Quarter End Offer' },
      { type: 'email_template', ref: 'tmpl-029', name: 'Industry Pulse Email' },
      { type: 'email_sequence', ref: 'tmpl-030', name: 'SDR Timed Follow Up' },
    ],
    tags: ['Event', 'Agent-Ready'],
    status: 'published',
    ai_ready: true,
    activation_rate: 54,
    updated_at: '2024-02-05T15:20:00Z',
  },
  {
    id: 'pb-011',
    title: 'High-Value Content Syndication',
    icon: '📚',
    summary:
      'Distribute premium content to target segments and convert engagement into sales conversations.',
    lifecycle: ['Acquire'],
    motion: ['Inbound', 'ABM'],
    stages: ['Awareness', 'Engagement'],
    primary_kpis: [
      'mqls_from_content',
      'sqls_from_content',
      'cost_per_opportunity',
    ],
    secondary_kpis: ['content_engagement', 'lead_quality'],
    industry_fit: ['SaaS', 'Tech'],
    personas: ['Marketing'],
    plays: ['play-001', 'play-004'],
    signals: [
      'content_consumed',
      'topic_interest',
      'repeat_downloads',
      'partner_referral',
    ],
    agents: ['agent-001'],
    kpis: ['MQLs from Content', 'SQLs from Content', 'Cost per Opportunity'],
    owner: {
      name: 'Sarah Chen',
      team: 'Marketing',
    },
    complexity: 'simple',
    duration_days: 21,
    expected_lift: '+16-20%',
    time_to_value: '14-21 days',
    data_needed: ['Intent', 'Enrichment'],
    triggers: [
      'Content consumed',
      'Topic interest',
      'Repeat downloads',
      'Partner referral',
    ],
    eligibility: {
      account_fit: ['ICP match', 'Content engagement'],
      buying_group_roles: ['Any'],
      exclusions: ['active_opportunity', 'do_not_contact'],
    },
    assets: [
      { type: 'landing_page', ref: 'tmpl-031', name: 'Content Lead Capture' },
      {
        type: 'email_sequence',
        ref: 'tmpl-032',
        name: 'SDR Content Follow Up',
      },
      { type: 'email_sequence', ref: 'tmpl-033', name: 'Nurture to Live Demo' },
    ],
    tags: ['Agent-Ready'],
    status: 'published',
    ai_ready: true,
    activation_rate: 59,
    updated_at: '2024-02-07T08:15:00Z',
  },
  {
    id: 'pb-012',
    title: 'Free-to-Paid Conversion',
    icon: '💎',
    summary:
      'Design automated journeys that move free tool users or trial participants into paid products based on usage thresholds and engagement patterns.',
    lifecycle: ['Close'],
    motion: ['PLG'],
    stages: ['Activation', 'Impact'],
    primary_kpis: ['trial_to_paid', 'arpa_lift', 'time_to_convert'],
    secondary_kpis: ['activation_rate', 'feature_adoption'],
    industry_fit: ['SaaS', 'Tech'],
    personas: ['Product', 'Sales'],
    plays: ['play-005'],
    signals: [
      'free_account_hit_usage_limit',
      'trial_3_days_from_end',
      'team_usage_expanded',
      'icp_fit',
    ],
    agents: ['agent-001', 'agent-004'],
    kpis: ['Trial to Paid', 'ARPA Lift', 'Time to Convert'],
    owner: {
      name: 'Mike Rodriguez',
      team: 'Product Growth',
    },
    complexity: 'standard',
    duration_days: 14,
    expected_lift: '+25-30%',
    time_to_value: '7-14 days',
    data_needed: ['Product Telemetry', 'Enrichment'],
    triggers: [
      'Free account hit usage limit',
      'Trial 3 days from end',
      'Team usage expanded',
      'ICP fit',
    ],
    eligibility: {
      account_fit: ['Active trial or free user', 'ICP match'],
      buying_group_roles: ['Economic Buyer', 'Champion'],
      exclusions: ['active_customer', 'opted_out'],
    },
    assets: [
      { type: 'email_template', ref: 'tmpl-034', name: 'Upgrade Nudge' },
      { type: 'script', ref: 'tmpl-035', name: 'AE Assisted Trial Close' },
      {
        type: 'email_template',
        ref: 'tmpl-036',
        name: 'Expansion to Team Plan',
      },
    ],
    tags: ['PLG', 'Agent-Ready'],
    status: 'published',
    ai_ready: true,
    activation_rate: 91,
    updated_at: '2024-02-10T11:30:00Z',
  },
]
// Mock Agents
export const mockAgents: Agent[] = [
  {
    id: 'agent-001',
    name: 'AI Email Agent',
    summary:
      'Generates and manages personalized multi-step email threads with handoff to AE',
    inputs: [
      'company_facts',
      'persona',
      'pain_points',
      'recent_signals',
      'content_assets',
    ],
    actions: [
      'draft',
      'send_if_confidence>0.8',
      'schedule_followup',
      'escalate',
    ],
    limits: {
      daily_sends: 200,
      auto_reply: true,
    },
    owner_team: 'Sales Ops',
    capabilities: ['personalize', 'followups', 'handoff', 'sentiment_analysis'],
  },
  {
    id: 'agent-002',
    name: 'Buying Group Detector',
    summary: 'Identifies and maps buying committee members across accounts',
    inputs: [
      'account_activity',
      'engagement_data',
      'org_chart',
      'intent_signals',
    ],
    actions: [
      'identify_roles',
      'map_influence',
      'score_engagement',
      'recommend_contacts',
    ],
    limits: {
      daily_sends: 0,
      auto_reply: false,
    },
    owner_team: 'RevOps',
    capabilities: ['role_detection', 'influence_mapping', 'engagement_scoring'],
  },
  {
    id: 'agent-003',
    name: 'Meeting Setter Agent',
    summary:
      'Automates meeting scheduling with intelligent calendar coordination',
    inputs: [
      'prospect_availability',
      'rep_calendar',
      'meeting_type',
      'timezone',
    ],
    actions: ['propose_times', 'send_invite', 'reschedule', 'send_reminders'],
    limits: {
      daily_sends: 500,
      auto_reply: true,
    },
    owner_team: 'Sales Ops',
    capabilities: [
      'calendar_sync',
      'timezone_detection',
      'automated_reminders',
    ],
  },
  {
    id: 'agent-004',
    name: 'Intent Scoring Agent',
    summary: 'Real-time scoring of account intent based on behavioral signals',
    inputs: [
      'web_activity',
      'content_downloads',
      'search_queries',
      'competitor_research',
    ],
    actions: [
      'calculate_score',
      'trigger_alerts',
      'update_segments',
      'recommend_plays',
    ],
    limits: {},
    owner_team: 'Marketing Ops',
    capabilities: [
      'real_time_scoring',
      'signal_aggregation',
      'trend_detection',
    ],
  },
  {
    id: 'agent-005',
    name: 'Data Enrichment Agent',
    summary: 'Enriches account and contact records with ZoomInfo data',
    inputs: ['company_name', 'domain', 'contact_email'],
    actions: [
      'enrich_firmographics',
      'enrich_technographics',
      'enrich_contact_info',
      'validate_data',
    ],
    limits: {},
    owner_team: 'RevOps',
    capabilities: [
      'firmographic_enrichment',
      'technographic_enrichment',
      'contact_enrichment',
      'data_validation',
    ],
  },
]
// Mock Plays
export const mockPlaysNew: Play[] = [
  {
    id: 'play-001',
    title: 'In-Market Account Takeover',
    summary:
      'Multi-channel blitz for accounts showing 3+ buying signals within 7 days',
    objective:
      'Book qualified meeting within 14 days of intent surge detection',
    stage: 'Engagement',
    motion: ['ABM', 'Outbound'],
    personas: ['CIO', 'VP Engineering', 'VP Operations'],
    account_tier: ['Tier 1', 'Tier 2'],
    trigger: {
      logic_md:
        'Account shows 3+ buying signals within 7 days AND intent score >= 70',
      data_sources: [
        'ZoomInfo Intent',
        'Website Activity',
        'Content Downloads',
      ],
      examples: [
        'Pricing page visits >= 2 within 7d',
        'Competitor comparison searches',
        'Product demo video watch >= 80%',
        'Technical documentation downloads',
      ],
    },
    eligibility: {
      account_fit: {
        tier: 'A',
        min_profile_score: 75,
      },
      buying_group_roles: ['Economic Buyer', 'Champion', 'Technical Buyer'],
      exclusions: ['open_opportunity', 'do_not_contact', 'recent_meeting_30d'],
    },
    audience: {
      personas: ['CIO', 'VP Engineering', 'VP Operations'],
      geo: ['NA', 'EU'],
    },
    channels: ['Email', 'LinkedIn', 'Ads', 'Phone'],
    steps: [
      {
        day: 0,
        type: 'AgentEmail',
        template_ref: 'tmpl-001',
        owner: 'AI Email Agent',
        sla_hours: 2,
      },
      {
        day: 1,
        type: 'LinkedInMessage',
        copy_ref: 'tmpl-002',
        owner: 'SDR',
        sla_hours: 24,
      },
      {
        day: 2,
        type: 'Ads',
        segment_ref: 'seg-001',
        owner: 'Marketing',
        sla_hours: 4,
      },
      {
        day: 3,
        type: 'Phone',
        script_ref: 'tmpl-003',
        owner: 'SDR',
        sla_hours: 48,
      },
      {
        day: 5,
        type: 'AgentEmail',
        template_ref: 'tmpl-004',
        owner: 'AI Email Agent',
        sla_hours: 2,
      },
    ],
    agents: [
      {
        name: 'AI Email Agent',
        capabilities: ['personalize', 'followups', 'handoff'],
        inputs: ['persona', 'pain_points', 'signal_context'],
      },
      {
        name: 'Intent Scoring Agent',
        capabilities: ['real_time_scoring', 'signal_aggregation'],
        inputs: ['web_activity', 'content_downloads'],
      },
    ],
    guardrails: {
      frequency_caps: {
        email_per_7d: 3,
      },
      exclusions: ['open_opportunity', 'do_not_contact', 'recent_meeting'],
    },
    measurement: {
      primary: ['meeting_rate', 'opportunity_rate'],
      secondary: ['response_rate', 'engagement_score'],
      success_definition_md:
        'Meeting rate +12-15% within 14 days compared to baseline',
      estimated_impact: 'Meeting rate +12-15%',
    },
    evidence: [
      {
        type: 'case_study',
        ref: 'https://example.com/case-study-001',
      },
    ],
    compliance: {
      gdpr_safe: true,
      can_spam: true,
    },
    taxonomy: {
      program_ids: ['prog-001'],
      playbook_ids: ['pb-001'],
      tags: ['High-Intent', 'Multi-Channel', 'ABM', 'AI-Powered'],
    },
    zoominfo_features: ['Intent', 'Enrichment', 'Copilot Chat'],
    complexity: 'standard',
    ai_readiness: 'native',
    time_to_implement: '3-5 days',
    performance_badge: 'Top Performer',
    source: 'zoominfo',
    updated_at: '2024-01-15T10:30:00Z',
    variants: [
      {
        name: 'PLG Variant',
        description: 'Adapted for product-led accounts with usage signals',
      },
    ],
    dependencies: ['Salesforce', 'ZoomInfo Intent', 'Email Platform'],
  },
  {
    id: 'play-002',
    title: 'Competitive Displacement',
    summary: 'Win deals from competitors with contract renewal approaching',
    objective: 'Convert competitive accounts within 60 days of renewal',
    stage: 'Evaluation',
    motion: ['ABM', 'Outbound'],
    personas: ['CIO', 'CFO/Finance', 'VP Operations'],
    account_tier: ['Tier 1'],
    trigger: {
      logic_md:
        'Competitor technology detected AND contract expires within 90 days',
      data_sources: [
        'ZoomInfo Technographics',
        'Intent Signals',
        'Account Research',
      ],
      examples: [
        'Competitor product detected in tech stack',
        'Contract renewal searches',
        'Alternative solution research',
      ],
    },
    eligibility: {
      account_fit: {
        tier: 'A',
        min_profile_score: 80,
      },
      buying_group_roles: ['Economic Buyer', 'Technical Buyer'],
      exclusions: ['active_customer', 'do_not_contact'],
    },
    audience: {
      personas: ['CIO', 'CFO/Finance', 'VP Operations'],
      geo: ['NA', 'EU'],
    },
    channels: ['Email', 'LinkedIn', 'Phone', 'Direct Mail'],
    steps: [
      {
        day: 0,
        type: 'Research',
        owner: 'Market Intelligence',
        sla_hours: 48,
      },
      {
        day: 2,
        type: 'AgentEmail',
        template_ref: 'tmpl-comp-001',
        owner: 'AI Email Agent',
        sla_hours: 4,
      },
      {
        day: 5,
        type: 'Phone',
        script_ref: 'tmpl-comp-002',
        owner: 'Account Executive',
        sla_hours: 72,
      },
      {
        day: 10,
        type: 'DirectMail',
        owner: 'Marketing',
        sla_hours: 168,
      },
    ],
    agents: [
      {
        name: 'Competitive Intelligence Agent',
        capabilities: ['tech_stack_analysis', 'contract_timing'],
        inputs: ['technographics', 'intent_signals'],
      },
      {
        name: 'AI Email Agent',
        capabilities: ['personalize', 'competitive_positioning'],
        inputs: ['competitor_info', 'persona', 'pain_points'],
      },
    ],
    guardrails: {
      frequency_caps: {
        email_per_7d: 2,
      },
      exclusions: [
        'active_customer',
        'do_not_contact',
        'legal_review_required',
      ],
    },
    measurement: {
      primary: ['win_rate', 'deal_velocity'],
      secondary: ['meeting_rate', 'proposal_rate'],
      success_definition_md: 'Win rate +20-25% vs baseline competitive deals',
      estimated_impact: 'Win rate +20-25%',
    },
    evidence: [],
    compliance: {
      gdpr_safe: true,
      can_spam: true,
    },
    taxonomy: {
      program_ids: ['prog-001'],
      playbook_ids: ['pb-001'],
      tags: ['Competitive', 'Enterprise', 'High-Value'],
    },
    zoominfo_features: ['Technographics', 'Intent', 'Enrichment'],
    complexity: 'advanced',
    ai_readiness: 'native',
    time_to_implement: '7-10 days',
    source: 'zoominfo',
    updated_at: '2024-01-18T14:20:00Z',
    dependencies: [
      'Salesforce',
      'ZoomInfo Technographics',
      'Direct Mail Provider',
    ],
  },
  {
    id: 'play-003',
    title: 'Buying Group Gap Fill',
    summary: 'Identify and engage missing buying committee members',
    objective: 'Complete buying group coverage within 21 days',
    stage: 'Evaluation',
    motion: ['ABM'],
    personas: ['CFO/Finance', 'IT Ops', 'Procurement'],
    account_tier: ['Tier 1', 'Tier 2'],
    trigger: {
      logic_md:
        'Active opportunity missing key buying group roles (Finance, IT, or Procurement)',
      data_sources: ['ZoomInfo Buying Groups', 'CRM Data', 'Org Charts'],
      examples: [
        'Opportunity in Stage 2+ with no Finance contact',
        'Technical buyer engaged but no IT Ops',
        'Missing procurement for enterprise deal',
      ],
    },
    eligibility: {
      account_fit: {
        tier: 'A',
        min_profile_score: 70,
      },
      buying_group_roles: ['Economic Buyer', 'Technical Buyer', 'Champion'],
      exclusions: ['stalled_opportunity', 'lost_deal'],
    },
    audience: {
      personas: ['CFO/Finance', 'IT Ops', 'Procurement'],
      geo: ['NA', 'EU', 'APAC'],
    },
    channels: ['Email', 'LinkedIn', 'Phone'],
    steps: [
      {
        day: 0,
        type: 'Enrichment',
        owner: 'Data Enrichment Agent',
        sla_hours: 4,
      },
      {
        day: 1,
        type: 'AgentEmail',
        template_ref: 'tmpl-bg-001',
        owner: 'AI Email Agent',
        sla_hours: 8,
      },
      {
        day: 3,
        type: 'LinkedInMessage',
        owner: 'Account Executive',
        sla_hours: 48,
      },
      {
        day: 7,
        type: 'Phone',
        script_ref: 'tmpl-bg-002',
        owner: 'Account Executive',
        sla_hours: 72,
      },
    ],
    agents: [
      {
        name: 'Buying Group Detector',
        capabilities: ['role_detection', 'influence_mapping', 'gap_analysis'],
        inputs: ['org_chart', 'engagement_data', 'intent_signals'],
      },
      {
        name: 'Data Enrichment Agent',
        capabilities: ['contact_enrichment', 'role_validation'],
        inputs: ['company_name', 'domain', 'target_roles'],
      },
      {
        name: 'AI Email Agent',
        capabilities: ['personalize', 'role_specific_messaging'],
        inputs: ['persona', 'buying_stage', 'existing_contacts'],
      },
    ],
    guardrails: {
      frequency_caps: {
        email_per_7d: 2,
      },
      exclusions: ['do_not_contact', 'opted_out'],
    },
    measurement: {
      primary: ['buying_group_coverage', 'deal_velocity'],
      secondary: ['response_rate', 'meeting_rate'],
      success_definition_md:
        'Complete buying group coverage in 80%+ of target opportunities',
      estimated_impact: 'Deal velocity +15-20%',
    },
    evidence: [],
    compliance: {
      gdpr_safe: true,
      can_spam: true,
    },
    taxonomy: {
      program_ids: ['prog-001'],
      playbook_ids: ['pb-001'],
      tags: ['Buying Groups', 'Deal Acceleration', 'ABM'],
    },
    zoominfo_features: ['Buying Groups', 'Enrichment', 'Org Charts'],
    complexity: 'standard',
    ai_readiness: 'native',
    time_to_implement: '5-7 days',
    performance_badge: 'High Impact',
    source: 'zoominfo',
    updated_at: '2024-01-20T09:15:00Z',
    dependencies: ['Salesforce', 'ZoomInfo Buying Groups', 'Email Platform'],
  },
  {
    id: 'play-004',
    title: 'Event Echo Campaign',
    summary: 'Multi-channel follow-up for event attendees and registrants',
    objective: 'Convert event engagement to qualified opportunities',
    stage: 'Awareness',
    motion: ['Event', 'ABM'],
    personas: ['CIO', 'VP Engineering', 'Marketing', 'Sales'],
    account_tier: ['Tier 1', 'Tier 2', 'Longtail'],
    trigger: {
      logic_md: 'Attended trade show, webinar, or virtual event within 7 days',
      data_sources: [
        'Event Platform',
        'Registration Data',
        'Session Attendance',
      ],
      examples: [
        'Attended booth at trade show',
        'Registered for webinar',
        'Attended breakout session',
        'Downloaded event content',
      ],
    },
    eligibility: {
      account_fit: {
        tier: 'B',
        min_profile_score: 60,
      },
      buying_group_roles: ['Any'],
      exclusions: ['active_customer', 'do_not_contact'],
    },
    audience: {
      personas: ['CIO', 'VP Engineering', 'Marketing', 'Sales'],
      geo: ['NA', 'EU', 'APAC'],
    },
    channels: ['Email', 'LinkedIn', 'Ads', 'Phone'],
    steps: [
      {
        day: 0,
        type: 'AgentEmail',
        template_ref: 'tmpl-event-001',
        owner: 'AI Email Agent',
        sla_hours: 24,
      },
      {
        day: 2,
        type: 'Ads',
        segment_ref: 'seg-event-001',
        owner: 'Marketing',
        sla_hours: 12,
      },
      {
        day: 3,
        type: 'LinkedInMessage',
        owner: 'SDR',
        sla_hours: 48,
      },
      {
        day: 7,
        type: 'Phone',
        script_ref: 'tmpl-event-002',
        owner: 'SDR',
        sla_hours: 72,
      },
    ],
    agents: [
      {
        name: 'Event Engagement Agent',
        capabilities: ['session_tracking', 'engagement_scoring'],
        inputs: ['event_data', 'session_attendance', 'content_downloads'],
      },
      {
        name: 'AI Email Agent',
        capabilities: ['personalize', 'event_followup'],
        inputs: ['event_context', 'session_attended', 'persona'],
      },
    ],
    guardrails: {
      frequency_caps: {
        email_per_7d: 3,
      },
      exclusions: ['do_not_contact', 'opted_out'],
    },
    measurement: {
      primary: ['meeting_rate', 'opportunity_rate'],
      secondary: ['response_rate', 'content_engagement'],
      success_definition_md: 'Meeting rate +30-35% vs non-event attendees',
      estimated_impact: 'Meeting rate +30-35%',
    },
    evidence: [],
    compliance: {
      gdpr_safe: true,
      can_spam: true,
    },
    taxonomy: {
      program_ids: ['prog-002'],
      playbook_ids: ['pb-002'],
      tags: ['Event', 'Field Marketing', 'Multi-Channel'],
    },
    zoominfo_features: ['Enrichment', 'Intent'],
    complexity: 'standard',
    ai_readiness: 'native',
    time_to_implement: '4-6 days',
    source: 'zoominfo',
    updated_at: '2024-01-22T11:45:00Z',
    dependencies: ['Event Platform', 'Email Platform', 'Ad Platform'],
  },
  {
    id: 'play-005',
    title: 'Product-Led to Human Assist',
    summary:
      'AI-powered handoff from self-serve to sales for high-value accounts',
    objective: 'Convert high-usage trial accounts to sales-assisted deals',
    stage: 'Activation',
    motion: ['PLG', 'Inbound'],
    personas: ['VP Engineering', 'IT Ops', 'CIO'],
    account_tier: ['Tier 1', 'Tier 2'],
    trigger: {
      logic_md:
        'Trial account with usage spike AND enterprise indicators (team size 10+, revenue $50M+)',
      data_sources: [
        'Product Telemetry',
        'ZoomInfo Firmographics',
        'Usage Analytics',
      ],
      examples: [
        'Usage increased 3x in 7 days',
        'Multiple team members active',
        'Enterprise features accessed',
        'High-value account profile',
      ],
    },
    eligibility: {
      account_fit: {
        tier: 'A',
        min_profile_score: 75,
      },
      buying_group_roles: ['Technical Buyer', 'Economic Buyer'],
      exclusions: ['active_sales_contact', 'opted_out_of_sales'],
    },
    audience: {
      personas: ['VP Engineering', 'IT Ops', 'CIO'],
      geo: ['NA', 'EU'],
    },
    channels: ['Email', 'In-app', 'Phone'],
    steps: [
      {
        day: 0,
        type: 'Enrichment',
        owner: 'Data Enrichment Agent',
        sla_hours: 2,
      },
      {
        day: 0,
        type: 'AgentEmail',
        template_ref: 'tmpl-plg-001',
        owner: 'AI Email Agent',
        sla_hours: 4,
      },
      {
        day: 1,
        type: 'InAppMessage',
        owner: 'Product',
        sla_hours: 12,
      },
      {
        day: 2,
        type: 'Phone',
        script_ref: 'tmpl-plg-002',
        owner: 'Account Executive',
        sla_hours: 48,
      },
    ],
    agents: [
      {
        name: 'Usage Analysis Agent',
        capabilities: ['usage_spike_detection', 'enterprise_signals'],
        inputs: ['product_telemetry', 'user_count', 'feature_usage'],
      },
      {
        name: 'AI Email Agent',
        capabilities: ['personalize', 'value_demonstration'],
        inputs: ['usage_data', 'persona', 'account_context'],
      },
      {
        name: 'Data Enrichment Agent',
        capabilities: ['firmographic_enrichment', 'buying_group_detection'],
        inputs: ['company_domain', 'user_data'],
      },
    ],
    guardrails: {
      frequency_caps: {
        email_per_7d: 2,
      },
      exclusions: ['opted_out_of_sales', 'active_sales_contact'],
    },
    measurement: {
      primary: ['trial_conversion_rate', 'expansion_revenue'],
      secondary: ['meeting_rate', 'time_to_conversion'],
      success_definition_md:
        'Trial-to-paid conversion +25-30% for enterprise accounts',
      estimated_impact: 'Conversion rate +25-30%',
    },
    evidence: [],
    compliance: {
      gdpr_safe: true,
      can_spam: true,
    },
    taxonomy: {
      program_ids: ['prog-002'],
      playbook_ids: ['pb-003'],
      tags: ['PLG', 'Enterprise', 'Conversion'],
    },
    zoominfo_features: ['Enrichment', 'Copilot Chat'],
    complexity: 'standard',
    ai_readiness: 'native',
    time_to_implement: '3-5 days',
    performance_badge: 'Quick Win',
    source: 'zoominfo',
    updated_at: '2024-01-25T08:30:00Z',
    dependencies: [
      'Product Analytics',
      'ZoomInfo Enrichment',
      'Email Platform',
    ],
  },
  {
    id: 'play-006',
    title: 'AI Products Expansion',
    summary: 'Upsell AI/data features to customers using basic plans',
    objective: 'Drive expansion revenue from AI product adoption',
    stage: 'Expansion',
    motion: ['Success', 'ABM'],
    personas: ['CIO', 'VP Engineering', 'IT Ops'],
    account_tier: ['Tier 1', 'Tier 2'],
    trigger: {
      logic_md:
        'Customer using data/AI features on basic plan with high engagement',
      data_sources: [
        'Product Telemetry',
        'Feature Usage',
        'Customer Health Score',
      ],
      examples: [
        'AI feature usage > 50% of quota',
        'Data API calls increasing',
        'Multiple users accessing AI tools',
        'High customer health score',
      ],
    },
    eligibility: {
      account_fit: {
        tier: 'A',
        min_profile_score: 80,
      },
      buying_group_roles: ['Economic Buyer', 'Champion'],
      exclusions: ['expansion_in_progress', 'at_risk_customer'],
    },
    audience: {
      personas: ['CIO', 'VP Engineering', 'IT Ops'],
      geo: ['NA', 'EU', 'APAC'],
    },
    channels: ['Email', 'In-app', 'Phone'],
    steps: [
      {
        day: 0,
        type: 'UsageAnalysis',
        owner: 'Customer Success',
        sla_hours: 24,
      },
      {
        day: 1,
        type: 'AgentEmail',
        template_ref: 'tmpl-exp-001',
        owner: 'AI Email Agent',
        sla_hours: 8,
      },
      {
        day: 3,
        type: 'InAppMessage',
        owner: 'Product',
        sla_hours: 12,
      },
      {
        day: 7,
        type: 'Phone',
        script_ref: 'tmpl-exp-002',
        owner: 'Account Manager',
        sla_hours: 72,
      },
    ],
    agents: [
      {
        name: 'Usage Analysis Agent',
        capabilities: [
          'feature_adoption_tracking',
          'expansion_opportunity_detection',
        ],
        inputs: ['feature_usage', 'user_count', 'api_calls'],
      },
      {
        name: 'AI Email Agent',
        capabilities: ['personalize', 'roi_demonstration'],
        inputs: ['usage_data', 'persona', 'expansion_options'],
      },
    ],
    guardrails: {
      frequency_caps: {
        email_per_7d: 2,
      },
      exclusions: ['at_risk_customer', 'expansion_in_progress'],
    },
    measurement: {
      primary: ['expansion_revenue', 'feature_adoption'],
      secondary: ['meeting_rate', 'proposal_rate'],
      success_definition_md: 'Expansion ACV +30-35% from AI product upsells',
      estimated_impact: 'Expansion ACV +30-35%',
    },
    evidence: [],
    compliance: {
      gdpr_safe: true,
      can_spam: true,
    },
    taxonomy: {
      program_ids: ['prog-002'],
      playbook_ids: ['pb-003'],
      tags: ['Expansion', 'AI Products', 'Customer Success'],
    },
    zoominfo_features: ['Copilot Chat', 'Account Summaries'],
    complexity: 'simple',
    ai_readiness: 'native',
    time_to_implement: '2-3 days',
    performance_badge: 'High Impact',
    source: 'zoominfo',
    updated_at: '2024-01-28T10:15:00Z',
    dependencies: ['Product Analytics', 'Email Platform', 'CRM'],
  },
  {
    id: 'play-007',
    title: 'Predictive Priority Routing',
    summary: 'Route high-propensity accounts to sales based on AI scoring',
    objective: 'Increase sales efficiency by prioritizing high-value accounts',
    stage: 'Engagement',
    motion: ['Inbound', 'ABM'],
    personas: ['CIO', 'VP Engineering', 'Marketing', 'Sales'],
    account_tier: ['Tier 1', 'Tier 2'],
    trigger: {
      logic_md:
        'Predictive model score >= 80 indicating high purchase propensity',
      data_sources: [
        'ZoomInfo Predictive',
        'Intent Signals',
        'Behavioral Data',
      ],
      examples: [
        'Model score 80+',
        'Multiple intent signals',
        'Similar to closed-won accounts',
        'High engagement score',
      ],
    },
    eligibility: {
      account_fit: {
        tier: 'A',
        min_profile_score: 70,
      },
      buying_group_roles: ['Any'],
      exclusions: ['active_opportunity', 'do_not_contact'],
    },
    audience: {
      personas: ['CIO', 'VP Engineering', 'Marketing', 'Sales'],
      geo: ['NA', 'EU'],
    },
    channels: ['Email', 'Phone', 'Ads'],
    steps: [
      {
        day: 0,
        type: 'Scoring',
        owner: 'Predictive Agent',
        sla_hours: 1,
      },
      {
        day: 0,
        type: 'Routing',
        owner: 'Sales Ops',
        sla_hours: 2,
      },
      {
        day: 0,
        type: 'AgentEmail',
        template_ref: 'tmpl-pred-001',
        owner: 'AI Email Agent',
        sla_hours: 4,
      },
      {
        day: 1,
        type: 'Phone',
        script_ref: 'tmpl-pred-002',
        owner: 'SDR',
        sla_hours: 24,
      },
    ],
    agents: [
      {
        name: 'Predictive Scoring Agent',
        capabilities: ['propensity_scoring', 'account_prioritization'],
        inputs: ['firmographics', 'intent_signals', 'behavioral_data'],
      },
      {
        name: 'AI Email Agent',
        capabilities: ['personalize', 'priority_messaging'],
        inputs: ['score_context', 'persona', 'account_data'],
      },
    ],
    guardrails: {
      frequency_caps: {
        email_per_7d: 3,
      },
      exclusions: ['active_opportunity', 'do_not_contact'],
    },
    measurement: {
      primary: ['meeting_rate', 'opportunity_rate'],
      secondary: ['response_rate', 'win_rate'],
      success_definition_md:
        'Meeting rate +40-45% for high-propensity accounts',
      estimated_impact: 'Meeting rate +40-45%',
    },
    evidence: [],
    compliance: {
      gdpr_safe: true,
      can_spam: true,
    },
    taxonomy: {
      program_ids: ['prog-001'],
      playbook_ids: ['pb-001'],
      tags: ['Predictive', 'AI-Powered', 'Sales Efficiency'],
    },
    zoominfo_features: ['Predictive', 'Intent', 'Copilot Chat'],
    complexity: 'standard',
    ai_readiness: 'native',
    time_to_implement: '4-6 days',
    performance_badge: 'Top Performer',
    source: 'zoominfo',
    updated_at: '2024-01-30T14:20:00Z',
    dependencies: ['ZoomInfo Predictive', 'CRM', 'Email Platform'],
  },
  {
    id: 'play-008',
    title: 'Renewal Risk Intervention',
    summary: 'Proactive outreach to at-risk customers showing negative signals',
    objective: 'Prevent churn by addressing issues before renewal',
    stage: 'Impact',
    motion: ['Success'],
    personas: ['CIO', 'CFO/Finance', 'IT Ops'],
    account_tier: ['Tier 1', 'Tier 2'],
    trigger: {
      logic_md:
        'Customer showing negative signals: usage drop, support tickets, or missing buying group roles',
      data_sources: [
        'Product Telemetry',
        'Support Tickets',
        'ZoomInfo Buying Groups',
      ],
      examples: [
        'Usage dropped 30%+ in 30 days',
        'Multiple negative support tickets',
        'Finance persona missing 60 days before renewal',
        'Low engagement score',
      ],
    },
    eligibility: {
      account_fit: {
        tier: 'A',
        min_profile_score: 70,
      },
      buying_group_roles: ['Economic Buyer', 'Champion'],
      exclusions: ['renewal_in_progress', 'expansion_in_progress'],
    },
    audience: {
      personas: ['CIO', 'CFO/Finance', 'IT Ops'],
      geo: ['NA', 'EU', 'APAC'],
    },
    channels: ['Email', 'Phone', 'In-app'],
    steps: [
      {
        day: 0,
        type: 'RiskAnalysis',
        owner: 'Customer Success',
        sla_hours: 12,
      },
      {
        day: 1,
        type: 'AgentEmail',
        template_ref: 'tmpl-risk-001',
        owner: 'AI Email Agent',
        sla_hours: 8,
      },
      {
        day: 2,
        type: 'Phone',
        script_ref: 'tmpl-risk-002',
        owner: 'Customer Success Manager',
        sla_hours: 24,
      },
      {
        day: 5,
        type: 'ExecutiveReview',
        owner: 'VP Customer Success',
        sla_hours: 72,
      },
    ],
    agents: [
      {
        name: 'Churn Risk Agent',
        capabilities: ['risk_detection', 'sentiment_analysis'],
        inputs: ['usage_data', 'support_tickets', 'engagement_score'],
      },
      {
        name: 'Buying Group Detector',
        capabilities: ['role_detection', 'gap_analysis'],
        inputs: ['org_chart', 'engagement_data'],
      },
      {
        name: 'AI Email Agent',
        capabilities: ['personalize', 'retention_messaging'],
        inputs: ['risk_context', 'persona', 'account_history'],
      },
    ],
    guardrails: {
      frequency_caps: {
        email_per_7d: 2,
      },
      exclusions: ['renewal_in_progress', 'expansion_in_progress'],
    },
    measurement: {
      primary: ['retention_rate', 'churn_prevention'],
      secondary: ['engagement_score', 'support_ticket_resolution'],
      success_definition_md:
        'Churn prevention rate +15-20% for at-risk accounts',
      estimated_impact: 'Churn prevention +15-20%',
    },
    evidence: [],
    compliance: {
      gdpr_safe: true,
      can_spam: true,
    },
    taxonomy: {
      program_ids: ['prog-002'],
      playbook_ids: ['pb-003'],
      tags: ['Retention', 'Customer Success', 'Risk Management'],
    },
    zoominfo_features: ['Buying Groups', 'Account Summaries'],
    complexity: 'advanced',
    ai_readiness: 'native',
    time_to_implement: '5-7 days',
    performance_badge: 'High Impact',
    source: 'zoominfo',
    updated_at: '2024-02-01T09:30:00Z',
    dependencies: [
      'Product Analytics',
      'Support System',
      'ZoomInfo Buying Groups',
    ],
  },
]
export const mockPrograms: Program[] = [
  {
    id: 1,
    name: 'Q1 Enterprise Growth',
    owner: 'Sarah Chen',
    motion: ['ABM', 'Outbound'],
    tags: ['Enterprise', 'North America', 'SaaS'],
    spend: 450000,
    headcount: 12,
    start_date: '2024-01-01',
    end_date: '2024-03-31',
    goals: {
      pipeline: 5000000,
      revenue: 1250000,
      cac_target: 15000,
      payback_target_months: 12,
    },
    playbook_ids: [1, 2],
    status: 'active',
    health: 'healthy',
  },
  {
    id: 2,
    name: 'PLG Activation Program',
    owner: 'Mike Rodriguez',
    motion: ['PLG', 'Inbound'],
    tags: ['SMB', 'Global', 'Tech'],
    spend: 280000,
    headcount: 8,
    start_date: '2024-01-01',
    end_date: '2024-06-30',
    goals: {
      pipeline: 3000000,
      revenue: 750000,
      cac_target: 8000,
      payback_target_months: 8,
    },
    playbook_ids: [3],
    status: 'active',
    health: 'at-risk',
  },
]
export const mockPlaybooks: Playbook[] = [
  {
    id: 1,
    name: 'Enterprise Account Blitz',
    program_id: 1,
    stage_focus: 'Acquire',
    kpi_weights: { meeting_rate: 0.4, pipeline: 0.6 },
    play_ids: [1, 2, 3],
    owner: 'Sarah Chen',
    last_run: '2024-01-15',
  },
  {
    id: 2,
    name: 'Executive Engagement',
    program_id: 1,
    stage_focus: 'Close',
    kpi_weights: { win_rate: 0.5, deal_velocity: 0.5 },
    play_ids: [4, 5],
    owner: 'Sarah Chen',
    last_run: '2024-01-20',
  },
  {
    id: 3,
    name: 'Trial to Paid Conversion',
    program_id: 2,
    stage_focus: 'Convert',
    kpi_weights: { activation: 0.3, conversion: 0.7 },
    play_ids: [6, 7, 8],
    owner: 'Mike Rodriguez',
    last_run: '2024-01-18',
  },
]
export const mockPlays: Play[] = [
  {
    id: 1,
    name: 'High-Intent Account Blitz',
    playbook_id: 1,
    stage: 'Acquire',
    trigger: '3+ buying signals within 7 days',
    eligibility: ['Enterprise segment', 'Tech vertical', 'North America'],
    objective: 'Book qualified meeting within 14 days',
    steps: [
      { name: 'Intent signal detection', owner: 'Marketing Ops', sla_hours: 2 },
      { name: 'Account research', owner: 'SDR', sla_hours: 4 },
      { name: 'Multi-channel outreach', owner: 'SDR', sla_hours: 24 },
      { name: 'Meeting booking', owner: 'SDR', sla_hours: 48 },
    ],
    assets: ['Intent report template', 'Outreach sequence', 'Meeting deck'],
    guardrails: [
      'Max 3 touches per week',
      'No weekend outreach',
      'Approved messaging only',
    ],
    stop_rules: [
      'Explicit opt-out',
      'No response after 5 touches',
      'Meeting booked',
    ],
    metrics: {
      target_uplift: 15,
      ttns_days: 14,
    },
    cost_per_run: 250,
    persona: ['VP Demand Gen', 'CMO'],
    channel: ['Email', 'LinkedIn', 'Phone'],
    complexity: 'Medium',
    is_pro: false,
  },
  {
    id: 2,
    name: 'Product-Led Growth Activation',
    playbook_id: 3,
    stage: 'Convert',
    trigger: 'Trial user hits activation milestone',
    eligibility: ['Active trial', 'Usage > 50%', 'Team size > 5'],
    objective: 'Convert to paid within trial period',
    steps: [
      { name: 'Activation detected', owner: 'Product', sla_hours: 1 },
      {
        name: 'Success email sent',
        owner: 'Marketing Automation',
        sla_hours: 2,
      },
      { name: 'CSM outreach', owner: 'Customer Success', sla_hours: 24 },
      { name: 'Upgrade offer', owner: 'Sales', sla_hours: 48 },
    ],
    assets: ['Activation email', 'ROI calculator', 'Upgrade deck'],
    guardrails: [
      'Respect trial period',
      'Value-first messaging',
      'No hard selling',
    ],
    stop_rules: ['Converted to paid', 'Trial expired', 'Churned'],
    metrics: {
      target_uplift: 10,
      ttns_days: 7,
    },
    cost_per_run: 150,
    persona: ['Product Manager', 'Engineering Lead'],
    channel: ['Email', 'In-app'],
    complexity: 'Easy',
    is_pro: false,
  },
  {
    id: 3,
    name: 'Executive Champion Nurture',
    playbook_id: 2,
    stage: 'Close',
    trigger: 'C-level prospect identified',
    eligibility: ['Enterprise segment', 'Multi-stakeholder deal', 'Stage 2+'],
    objective: 'Accelerate deal progression',
    steps: [
      { name: 'Executive research', owner: 'Account Executive', sla_hours: 8 },
      { name: 'Personalized content', owner: 'Content Team', sla_hours: 48 },
      { name: 'Executive briefing', owner: 'Sales Leader', sla_hours: 72 },
      {
        name: 'Business case review',
        owner: 'Solutions Consultant',
        sla_hours: 96,
      },
    ],
    assets: ['Executive brief template', 'Industry insights', 'ROI model'],
    guardrails: [
      'Senior stakeholder only',
      'High-value content',
      'Quarterly cadence max',
    ],
    stop_rules: ['Deal closed', 'Champion churned', 'No engagement 60 days'],
    metrics: {
      target_uplift: 25,
      ttns_days: 45,
    },
    cost_per_run: 800,
    persona: ['CEO', 'CFO', 'CRO'],
    channel: ['Email', 'Executive dinner', 'Custom content'],
    complexity: 'High',
    is_pro: true,
  },
  {
    id: 4,
    name: 'Inbound Lead Scoring & Routing',
    playbook_id: 1,
    stage: 'Acquire',
    trigger: 'Form submission or demo request',
    eligibility: ['All inbound leads', 'Complete profile', 'Valid email'],
    objective: 'Route to sales within 5 minutes',
    steps: [
      { name: 'Lead scoring', owner: 'Marketing Automation', sla_hours: 0.1 },
      { name: 'Territory assignment', owner: 'RevOps', sla_hours: 0.1 },
      { name: 'SDR notification', owner: 'System', sla_hours: 0.1 },
      { name: 'First touch', owner: 'SDR', sla_hours: 0.5 },
    ],
    assets: ['Scoring model', 'Territory rules', 'Response templates'],
    guardrails: ['5-minute SLA', 'Round-robin by capacity', 'Duplicate check'],
    stop_rules: ['Assigned to rep', 'Disqualified', 'Duplicate'],
    metrics: {
      target_uplift: 40,
      ttns_days: 1,
    },
    cost_per_run: 50,
    persona: ['All personas'],
    channel: ['Email', 'Phone'],
    complexity: 'Medium',
    is_pro: false,
  },
  {
    id: 5,
    name: 'Healthcare Compliance Outreach',
    playbook_id: null,
    stage: 'Acquire',
    trigger: 'Healthcare account enters TAM',
    eligibility: ['Healthcare vertical', 'HIPAA compliant', '500+ employees'],
    objective: 'Generate qualified pipeline',
    steps: [
      { name: 'Compliance review', owner: 'Legal', sla_hours: 48 },
      { name: 'Account research', owner: 'SDR', sla_hours: 24 },
      { name: 'Compliant outreach', owner: 'SDR', sla_hours: 72 },
      { name: 'Security briefing', owner: 'Solutions Engineer', sla_hours: 96 },
    ],
    assets: ['HIPAA collateral', 'Security whitepaper', 'Compliance checklist'],
    guardrails: [
      'HIPAA compliant only',
      'Approved messaging',
      'Legal review required',
    ],
    stop_rules: ['Meeting booked', 'Opt-out', 'Non-compliant'],
    metrics: {
      target_uplift: 8,
      ttns_days: 21,
    },
    cost_per_run: 450,
    persona: ['CISO', 'VP IT', 'Compliance Officer'],
    channel: ['Email', 'LinkedIn'],
    complexity: 'Hard',
    is_pro: true,
  },
  {
    id: 6,
    name: 'Event-Driven Pipeline',
    playbook_id: null,
    stage: 'Acquire',
    trigger: 'Event registration or attendance',
    eligibility: ['Target account', 'Event attendee', 'Engaged session'],
    objective: 'Convert to qualified opportunity',
    steps: [
      { name: 'Pre-event targeting', owner: 'Marketing', sla_hours: 168 },
      { name: 'Event engagement', owner: 'Field Marketing', sla_hours: 8 },
      { name: 'Post-event follow-up', owner: 'SDR', sla_hours: 24 },
      {
        name: 'Opportunity creation',
        owner: 'Account Executive',
        sla_hours: 72,
      },
    ],
    assets: ['Event invite', 'Booth materials', 'Follow-up sequence'],
    guardrails: [
      'Event-specific messaging',
      'Timely follow-up',
      'Value-first approach',
    ],
    stop_rules: ['Opportunity created', 'No interest', '30 days elapsed'],
    metrics: {
      target_uplift: 35,
      ttns_days: 30,
    },
    cost_per_run: 1200,
    persona: ['All personas'],
    channel: ['Email', 'Event', 'Phone'],
    complexity: 'High',
    is_pro: true,
  },
  {
    id: 7,
    name: 'Usage-Based Expansion',
    playbook_id: null,
    stage: 'Expand',
    trigger: 'Customer usage exceeds 80% of plan',
    eligibility: ['Active customer', 'Good health score', 'Usage trending up'],
    objective: 'Drive expansion revenue',
    steps: [
      { name: 'Usage analysis', owner: 'Customer Success', sla_hours: 24 },
      {
        name: 'Expansion opportunity',
        owner: 'Account Manager',
        sla_hours: 48,
      },
      { name: 'Business case', owner: 'Solutions Consultant', sla_hours: 72 },
      { name: 'Expansion close', owner: 'Account Executive', sla_hours: 168 },
    ],
    assets: ['Usage report', 'Expansion deck', 'ROI calculator'],
    guardrails: [
      'Customer health check',
      'Value demonstration',
      'Renewal timing',
    ],
    stop_rules: ['Expansion closed', 'Customer churned', 'No budget'],
    metrics: {
      target_uplift: 30,
      ttns_days: 21,
    },
    cost_per_run: 350,
    persona: ['Economic Buyer', 'Champion'],
    channel: ['Email', 'QBR', 'Phone'],
    complexity: 'Medium',
    is_pro: false,
  },
  {
    id: 8,
    name: 'Competitive Displacement',
    playbook_id: null,
    stage: 'Acquire',
    trigger: 'Competitor contract renewal approaching',
    eligibility: [
      'Using competitor',
      'Contract expires <90 days',
      'Target segment',
    ],
    objective: 'Win competitive deal',
    steps: [
      {
        name: 'Competitive intelligence',
        owner: 'Market Intelligence',
        sla_hours: 48,
      },
      { name: 'Comparison content', owner: 'Product Marketing', sla_hours: 72 },
      { name: 'Targeted outreach', owner: 'Account Executive', sla_hours: 96 },
      { name: 'Proof of value', owner: 'Solutions Engineer', sla_hours: 168 },
    ],
    assets: ['Competitive matrix', 'Migration guide', 'ROI comparison'],
    guardrails: ['Fact-based claims', 'No disparagement', 'Legal review'],
    stop_rules: ['Deal won', 'Competitor renewed', 'Not interested'],
    metrics: {
      target_uplift: 22,
      ttns_days: 60,
    },
    cost_per_run: 950,
    persona: ['VP', 'Director'],
    channel: ['Email', 'LinkedIn', 'Phone'],
    complexity: 'High',
    is_pro: true,
  },
]
export const mockFunnelModels: Record<number, FunnelModel> = {
  1: {
    program_id: 1,
    stages_acquire: {
      impressions: 500000,
      clicks: 25000,
      mql: 5000,
      sal: 2500,
      sql: 1250,
      opp: 500,
    },
    stages_close: {
      stage1: 500,
      stage2: 350,
      commit: 200,
      won: 125,
    },
    stages_expand: {
      activation: 100,
      expansion_opp: 60,
      expansion_won: 40,
    },
    rates: {
      impr_to_click: 5.0,
      click_to_mql: 20.0,
      mql_to_sal: 50.0,
      sal_to_sql: 50.0,
      sql_to_opp: 40.0,
      opp_to_stage1: 100.0,
      stage1_to_stage2: 70.0,
      stage2_to_commit: 57.1,
      commit_to_won: 62.5,
      activation_to_exp_opp: 60.0,
      exp_opp_to_won: 66.7,
    },
    velocities: {
      mql_to_sql_days: 14,
      sql_to_opp_days: 21,
      opp_to_won_days: 45,
    },
    avg_deal_size: 50000,
    expansion_uplift: 30,
    attribution_split: {
      inbound: 30,
      outbound: 40,
      plg: 10,
      partner: 15,
      field: 5,
    },
  },
  2: {
    program_id: 2,
    stages_acquire: {
      impressions: 300000,
      clicks: 18000,
      mql: 4500,
      sal: 2250,
      sql: 1125,
      opp: 450,
    },
    stages_close: {
      stage1: 450,
      stage2: 315,
      commit: 189,
      won: 113,
    },
    stages_expand: {
      activation: 90,
      expansion_opp: 54,
      expansion_won: 36,
    },
    rates: {
      impr_to_click: 6.0,
      click_to_mql: 25.0,
      mql_to_sal: 50.0,
      sal_to_sql: 50.0,
      sql_to_opp: 40.0,
      opp_to_stage1: 100.0,
      stage1_to_stage2: 70.0,
      stage2_to_commit: 60.0,
      commit_to_won: 60.0,
      activation_to_exp_opp: 60.0,
      exp_opp_to_won: 66.7,
    },
    velocities: {
      mql_to_sql_days: 10,
      sql_to_opp_days: 14,
      opp_to_won_days: 30,
    },
    avg_deal_size: 25000,
    expansion_uplift: 25,
    attribution_split: {
      inbound: 40,
      outbound: 20,
      plg: 30,
      partner: 5,
      field: 5,
    },
  },
}
export const mockInsights = [
  {
    id: 1,
    type: 'warning',
    title: 'MQL to SAL SLA Breach',
    description:
      'Q1 Enterprise Growth program has 15% of MQLs exceeding 24-hour SAL conversion SLA',
    program_id: 1,
    severity: 'medium',
    action: 'Review routing rules and SDR capacity',
  },
  {
    id: 2,
    type: 'alert',
    title: 'Finance Persona Missing',
    description:
      'Enterprise Account Blitz playbook has no plays targeting CFO/Finance personas',
    playbook_id: 1,
    severity: 'high',
    action: 'Add executive engagement play',
  },
  {
    id: 3,
    type: 'info',
    title: 'Fatigue Threshold Reached',
    description:
      'High-Intent Account Blitz play has reached weekly touch limit for 8 accounts',
    play_id: 1,
    severity: 'low',
    action: 'Accounts automatically paused for 7 days',
  },
  {
    id: 4,
    type: 'success',
    title: 'Bandit Reallocating to Variant B',
    description:
      'PLG Activation play variant B showing 18% higher conversion, increasing allocation',
    play_id: 2,
    severity: 'low',
    action: 'No action needed - automatic optimization',
  },
]
export const userPlan = {
  name: 'Growth',
  limits: {
    programs: 5,
    playbooks: Infinity,
    plays: 50,
    advanced_reporting: true,
    abm_segments: true,
    event_triggers: true,
    copilot: false,
    partner_plays: false,
  },
  usage: {
    programs: 2,
    playbooks: 3,
    plays: 8,
  },
}
